import { functions } from "../firebase";

// Client wrappers for the first-party TikTok callables (functions/src/
// tiktokPublishing.ts). Tokens NEVER cross this boundary: the connection read
// returns only display chrome, and publishing is done entirely server-side.

export type TikTokConnection = {
  connected: boolean;
  displayName?: string;
  avatarUrl?: string;
  scopes?: string[];
};

export type TikTokPublishResult = {
  ok: boolean;
  publishId?: string;
};

export type TikTokStatusResult = {
  status: string;
  publishId: string;
  raw?: unknown;
};

// Read the community's TikTok connection state for the connect panel.
export const getTikTokConnection = async (
  communityId: string
): Promise<TikTokConnection> => {
  const callable = functions.httpsCallable("tiktokGetConnection");
  const res = await callable({ communityId });
  return res.data as TikTokConnection;
};

// Start Login Kit: returns the authorize URL to open in a popup.
export const startTikTokAuth = async (
  communityId: string
): Promise<{ url: string }> => {
  const callable = functions.httpsCallable("tiktokStartAuth");
  const res = await callable({ communityId });
  return res.data as { url: string };
};

// Finish Login Kit: the /auth/tiktok/callback route calls this with the
// ?code and ?state TikTok returned.
export const completeTikTokAuth = async (params: {
  code: string;
  state: string;
}): Promise<TikTokConnection> => {
  const callable = functions.httpsCallable("tiktokCompleteAuth");
  const res = await callable(params);
  return res.data as TikTokConnection;
};

// Push a carousel project (or explicit imageUrls) to the coach's TikTok inbox.
export const publishTikTokCarousel = async (params: {
  communityId: string;
  projectId?: string;
  imageUrls?: string[];
  caption?: string;
}): Promise<TikTokPublishResult> => {
  const callable = functions.httpsCallable("tiktokPublishCarousel", {
    timeout: 120000,
  });
  const res = await callable(params);
  return res.data as TikTokPublishResult;
};

// Poll a publish's status (by publishId or a project that stores one).
export const getTikTokPostStatus = async (params: {
  communityId: string;
  publishId?: string;
  projectId?: string;
}): Promise<TikTokStatusResult> => {
  const callable = functions.httpsCallable("tiktokPostStatus");
  const res = await callable(params);
  return res.data as TikTokStatusResult;
};

// Revoke + clear the community's TikTok connection.
export const disconnectTikTok = async (
  communityId: string
): Promise<{ connected: boolean }> => {
  const callable = functions.httpsCallable("tiktokDisconnect");
  const res = await callable({ communityId });
  return res.data as { connected: boolean };
};
